import { useState, useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import {
  IconButton,
  Popover,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  Button,
  Chip,
  Tooltip,
} from '@mui/material'
import {
  Notifications as NotificationsIcon,
  Comment as CommentIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Update as UpdateIcon,
  Close as CloseIcon,
  Circle as CircleIcon,
} from '@mui/icons-material'
import { formatDistanceToNow } from 'date-fns'
import { useNavigate } from 'react-router-dom'
import { RootState } from '../../store'
import { getInitials } from '../../utils/userColors'
import CountBadge from '../../shared-components/CountBadge'

interface NotificationItem {
  id: string
  type: 'comment' | 'status_change' | 'assignment' | 'update' | 'new_issue' | 'system'
  title: string
  message: string
  timestamp: string
  read: boolean
  issueKey?: string
  issueTitle?: string
  projectId?: string
  projectName?: string
  actionBy?: {
    name: string
    avatar?: string
  }
}

const NotificationCenter: React.FC = () => {
  const navigate = useNavigate()
  const { projects } = useSelector((state: RootState) => state.projects)
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [notifications, setNotifications] = useState<NotificationItem[]>([])
  
  const open = Boolean(anchorEl)

  // Helper function to get project slug from project name
  const getProjectSlug = (projectName: string): string => {
    return projectName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
  }

  // Load notifications from localStorage on mount (only once)
  const hasLoadedRef = useRef(false)
  useEffect(() => {
    if (hasLoadedRef.current) return
    hasLoadedRef.current = true
    
    const stored = localStorage.getItem('vexa_notifications')
    if (stored) {
      try {
        const parsed = JSON.parse(stored)
        // Only set if we have valid notifications and state is empty
        if (Array.isArray(parsed) && parsed.length > 0) {
          // Clean up old notifications that might have user names in messages
          const cleaned = parsed.map(n => {
            // Remove user names from messages if they exist
            if (n.message && n.actionBy?.name) {
              // Remove patterns like "Raghava CVS: " or "Raghava CVS created" from message
              const namePattern = new RegExp(`^${n.actionBy.name}[\\s:]+`, 'i')
              n.message = n.message.replace(namePattern, '')
              // Also remove "created" if it's at the start
              n.message = n.message.replace(/^created\s+/i, '')
            }
            return n
          })
          setNotifications(cleaned)
          console.log('NotificationCenter: Loaded', cleaned.length, 'notifications from localStorage')
        } else {
          // Clear invalid or empty data
          localStorage.removeItem('vexa_notifications')
        }
      } catch (e) {
        console.error('Failed to parse stored notifications:', e)
        localStorage.removeItem('vexa_notifications')
      }
    }
  }, [])

  // Save notifications to localStorage whenever they change
  useEffect(() => {
    if (notifications.length > 0) {
      localStorage.setItem('vexa_notifications', JSON.stringify(notifications))
    } else {
      // Explicitly clear localStorage when notifications array is empty
      localStorage.removeItem('vexa_notifications')
    }
  }, [notifications])

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleNotificationClick = (notification: NotificationItem) => {
    // Mark as read
    setNotifications(prev =>
      prev.map(n => n.id === notification.id ? { ...n, read: true } : n)
    )

    // Navigate to issue if available
    if (notification.issueKey) {
      let projectSlug = notification.projectId || ''
      
      // Try to get project slug from project name if available
      if (notification.projectName) {
        projectSlug = getProjectSlug(notification.projectName)
      } else if (notification.projectId) {
        // Try to find project in Redux state to get the name
        const project = projects.find(p => p._id === notification.projectId)
        if (project) {
          projectSlug = getProjectSlug(project.name)
        } else {
          // Fallback: use project ID if project not found
          projectSlug = notification.projectId
        }
      }
      
      if (projectSlug && notification.issueKey) {
        navigate(`/projects/${projectSlug}/kanban/issues/${notification.issueKey}`)
        handleClose()
      }
    }
  }

  const handleMarkAllRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })))
  }

  const handleClearAll = () => {
    setNotifications([])
    // localStorage will be cleared by the useEffect when notifications becomes empty
    localStorage.removeItem('vexa_notifications')
  }

  const handleRemoveNotification = (id: string, event: React.MouseEvent) => {
    event.stopPropagation()
    setNotifications(prev => prev.filter(n => n.id !== id))
  }

  // Listen for new notifications from socket events
  useEffect(() => {
    const handleNewNotification = (event: CustomEvent<NotificationItem>) => {
      console.log('NotificationCenter: Received new notification event', event.detail)
      const notification = event.detail
      setNotifications(prev => {
        // Check if notification already exists (deduplicate by id)
        const existsById = prev.some(n => n.id === notification.id)
        if (existsById) {
          console.log('NotificationCenter: Notification already exists by ID, skipping', notification.id)
          return prev
        }
        
        // Also check for duplicate by content (same issue key + type + message within
        // last 30 seconds). Message is included so two distinct system-wide
        // notifications (issueKey/projectId both undefined for those) aren't wrongly
        // collapsed into one just because they share the same undefined fields.
        const now = Date.now()
        const notificationTime = new Date(notification.timestamp).getTime()
        const isDuplicate = prev.some(n => {
          if (n.issueKey === notification.issueKey &&
              n.type === notification.type &&
              n.projectId === notification.projectId &&
              n.message === notification.message) {
            const nTime = new Date(n.timestamp).getTime()
            // If same type of notification for same issue within 30 seconds, consider it duplicate
            if (Math.abs(now - nTime) < 30000 || Math.abs(notificationTime - nTime) < 30000) {
              return true
            }
          }
          return false
        })
        
        if (isDuplicate) {
          console.log('NotificationCenter: Duplicate notification detected (same issue + type within 30s), skipping')
          return prev
        }
        
        // Add new notification at the beginning, keep last 50
        const updated = [notification, ...prev].slice(0, 50)
        console.log('NotificationCenter: Updated notifications', updated.length)
        return updated
      })
    }

    console.log('NotificationCenter: Setting up notification listener')
    window.addEventListener('new-notification', handleNewNotification as EventListener)

    return () => {
      console.log('NotificationCenter: Cleaning up notification listener')
      window.removeEventListener('new-notification', handleNewNotification as EventListener)
    }
  }, [])

  const unreadCount = notifications.filter(n => !n.read).length

  const getNotificationIcon = (type: NotificationItem['type']) => {
    switch (type) {
      case 'comment':
        return <CommentIcon fontSize="small" />
      case 'status_change':
        return <CheckCircleIcon fontSize="small" />
      case 'assignment':
        return <AssignmentIcon fontSize="small" />
      case 'update':
        return <UpdateIcon fontSize="small" />
      case 'new_issue':
        return <CircleIcon fontSize="small" />
      default:
        return <NotificationsIcon fontSize="small" />
    }
  }

  const getNotificationColor = (type: NotificationItem['type']) => {
    switch (type) {
      case 'comment':
        return '#3b82f6'
      case 'status_change':
        return '#10b981'
      case 'assignment':
        return '#f59e0b'
      case 'update':
        return '#6366f1'
      case 'new_issue':
        return '#8b5cf6'
      default:
        return '#64748b'
    }
  }

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton
          onClick={handleClick}
          sx={{
            color: '#64748b',
            '&:hover': {
              backgroundColor: '#f1f5f9',
              color: '#3b82f6',
            }
          }}
        >
          <CountBadge count={unreadCount} color="error" max={99}>
            <NotificationsIcon />
          </CountBadge>
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            width: 380,
            maxWidth: '90vw',
            maxHeight: 600,
            mt: 1,
            borderRadius: 2,
            boxShadow: '0 8px 24px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.08)',
            border: '1px solid #e2e8f0',
          }
        }}
      >
        <Box sx={{ p: 1.5, borderBottom: '1px solid #e2e8f0', backgroundColor: '#fafbfc' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '0.9375rem' }}>
                Notifications
              </Typography>
              {unreadCount > 0 && (
                <Chip
                  label={unreadCount}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    bgcolor: 'error.main',
                    color: 'white',
                  }}
                />
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {unreadCount > 0 && (
                <Button 
                  size="small" 
                  onClick={handleMarkAllRead} 
                  sx={{ 
                    textTransform: 'none',
                    fontSize: '0.75rem',
                    px: 1,
                    minWidth: 'auto',
                  }}
                >
                  Mark all read
                </Button>
              )}
              {notifications.length > 0 && (
                <Button 
                  size="small" 
                  onClick={handleClearAll} 
                  sx={{ 
                    textTransform: 'none', 
                    color: 'error.main',
                    fontSize: '0.75rem',
                    px: 1,
                    minWidth: 'auto',
                    '&:hover': {
                      bgcolor: 'error.light',
                    }
                  }}
                >
                  Clear all
                </Button>
              )}
            </Box>
          </Box>
        </Box>

        <Box sx={{ maxHeight: 500, overflowY: 'auto' }}>
          {notifications.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <NotificationsIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                No notifications yet
              </Typography>
            </Box>
          ) : (
            <List sx={{ p: 0 }}>
              {notifications.map((notification, index) => (
                <Box key={notification.id}>
                  <ListItem
                    button
                    onClick={() => handleNotificationClick(notification)}
                    sx={{
                      py: 1,
                      px: 1.5,
                      backgroundColor: notification.read ? 'transparent' : '#f8fafc',
                      '&:hover': {
                        backgroundColor: '#f1f5f9',
                      },
                      position: 'relative',
                      flexDirection: 'column',
                      alignItems: 'stretch',
                    }}
                  >
                    {/* Main content row */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, width: '100%', pr: 3 }}>
                      {/* Icon indicator */}
                      <Box
                        sx={{
                          width: 4,
                          minWidth: 4,
                          height: '100%',
                          minHeight: 40,
                          borderRadius: '0 2px 2px 0',
                          bgcolor: notification.read ? 'transparent' : getNotificationColor(notification.type),
                          mt: 0.5,
                        }}
                      />
                      
                      {/* Content */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        {/* Title and issue key */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: notification.read ? 500 : 600,
                              fontSize: '0.875rem',
                              flex: 1,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {notification.title}
                          </Typography>
                          {!notification.read && (
                            <CircleIcon sx={{ fontSize: 6, color: '#3b82f6', flexShrink: 0 }} />
                          )}
                          {notification.issueKey && (
                            <Chip
                              label={notification.issueKey}
                              size="small"
                              sx={{
                                height: 18,
                                fontSize: '0.65rem',
                                fontWeight: 600,
                                px: 0.5,
                                flexShrink: 0,
                              }}
                            />
                          )}
                        </Box>
                        
                        {/* Message */}
                        <Typography 
                          variant="body2" 
                          color="text.secondary" 
                          sx={{ 
                            fontSize: '0.8125rem',
                            lineHeight: 1.4,
                            mb: 0.5,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {notification.message}
                        </Typography>
                        
                        {/* Bottom row: timestamp and user info */}
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                          <Typography 
                            variant="caption" 
                            color="text.secondary"
                            sx={{ fontSize: '0.75rem' }}
                          >
                            {formatDistanceToNow(new Date(notification.timestamp), { addSuffix: true })}
                          </Typography>
                          
                          {/* User info - bottom right with dark color and bold */}
                          {notification.actionBy && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                              <Avatar
                                sx={{
                                  width: 18,
                                  height: 18,
                                  bgcolor: getNotificationColor(notification.type),
                                  border: '1px solid rgba(0,0,0,0.1)',
                                }}
                              >
                                {notification.actionBy?.avatar ? (
                                  <img 
                                    src={notification.actionBy.avatar} 
                                    alt={notification.actionBy.name || ''} 
                                    style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} 
                                    onError={(e) => {
                                      // Fallback to initial if image fails to load
                                      const target = e.target as HTMLImageElement
                                      target.style.display = 'none'
                                      const parent = target.parentElement
                                      if (parent) {
                                        const fallback = document.createElement('span')
                                        fallback.textContent = getInitials(notification.actionBy?.name, 'U')
                                        fallback.style.fontSize = '0.6rem'
                                        fallback.style.fontWeight = '600'
                                        parent.appendChild(fallback)
                                      }
                                    }}
                                  />
                                ) : (
                                  <Typography sx={{ fontSize: '0.6rem', fontWeight: 600 }}>
                                    {getInitials(notification.actionBy?.name, 'U')}
                                  </Typography>
                                )}
                              </Avatar>
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  fontSize: '0.75rem',
                                  fontWeight: 600,
                                  color: '#1e293b',
                                }}
                              >
                                {notification.actionBy.name}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Box>
                      
                      {/* Close button */}
                      <IconButton
                        size="small"
                        onClick={(e) => handleRemoveNotification(notification.id, e)}
                        sx={{
                          position: 'absolute',
                          top: 4,
                          right: 4,
                          width: 20,
                          height: 20,
                          opacity: 0.4,
                          '&:hover': {
                            opacity: 1,
                            color: 'error.main',
                            backgroundColor: 'error.light',
                          }
                        }}
                      >
                        <CloseIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Box>
                  </ListItem>
                  {index < notifications.length - 1 && <Divider sx={{ mx: 1.5 }} />}
                </Box>
              ))}
            </List>
          )}
        </Box>
      </Popover>
    </>
  )
}

export default NotificationCenter

